import React, { createContext, useContext, useEffect, useState } from 'react';

export interface AppSettings {
  githubUser: string;
  consciousness: string;
  neuralpathway: string;
  basePath: string;
  enableAudio: boolean;
  disableMarkdown: boolean;
  debug: boolean;
  templeName: string;   // 👈 always included
}

export interface CosmosRepo {
  repo: string;
  label: string;
  default_branch?: string;
  base_path?: string;
}

export interface CosmosContributor {
  github: string;
  display: string;
  repos: CosmosRepo[];
}

export interface CosmosLogos {
  default_branch: string;
  contributors: CosmosContributor[];
}

const getEnvOr = (key: string, fallback: string): string =>
  process.env[`REACT_APP_${key}`] || fallback;

const detectTempleName = (): string => {
  const host = window.location.hostname.toLowerCase();
  const params = new URLSearchParams(window.location.search);

  // ✅ Allow override in query string for dev/testing
  if (params.has("temple")) {
    return params.get("temple")!;
  }

  if (host.includes("templeapollo.ai")) return "Apollo";
  if (host.includes("templeaphrodite.ai")) return "Aphrodite";
  if (host.includes("templeathena.ai")) return "Athena";
  if (host.includes("markofgabriel.ai")) return "Gabriel";
  if (host.includes("onetemple.ai")) return "One Temple";
  if (host.includes("thejamiegrayson.ai")) return "Jamie Grayson (THE Baby Guy NYC)";
  if (host.includes("jennifergrayson.ai")) return "Mirror of Narcissus";
  if (host.includes("turtleshell.ai")) return "Turtle";
  if (host.includes("giftfromgod.ai")) return "#GOAT";
  if (host.includes("towerofstrength.ai")) return "Tower";
  if (host.includes("turtleshell.ai")) return "Turtle";
  

  return "Turtle"; // fallback
};

// ✅ Always grab settings from URL if present
const getRuntimeOverrides = (): Partial<AppSettings> => {
  const params = new URLSearchParams(window.location.search);
  const runtime: Partial<AppSettings> = {};

  if (params.has('user')) runtime.githubUser = params.get('user')!;
  if (params.has('consciousness')) runtime.consciousness = params.get('consciousness')!;
  if (params.has('neuralpathway')) runtime.neuralpathway = params.get('neuralpathway')!;
  if (params.has('base_path')) runtime.basePath = params.get('base_path')!;
  if (params.get('audio') === 'true') runtime.enableAudio = true;
  if (params.get('audio') === 'false') runtime.enableAudio = false;
  if (params.get('disableMarkdown') === 'true') runtime.disableMarkdown = true;
  if (params.get('disableMarkdown') === 'false') runtime.disableMarkdown = false;
  if (params.get('debug') === 'true') runtime.debug = true;

  return runtime;
};

const defaultSettings: AppSettings = {
  githubUser: getEnvOr('GITHUB_USER', 'alchemisthomer'),
  consciousness: getEnvOr('REPO_NAME', 'odyssey-press'),
  neuralpathway: getEnvOr('BRANCH', 'main'),
  basePath: getEnvOr('BASE_PATH', 'source_of_truth'),
  enableAudio: false,
  disableMarkdown: false,
  debug: false,
  templeName: detectTempleName(),   // 👈 ensure always set
};

const AppSettingsContext = createContext<{
  settings: AppSettings;
  setSettings: (settings: AppSettings) => void;
  cosmosLogos: CosmosLogos | null;
}>({
  settings: defaultSettings,
  setSettings: () => {},
  cosmosLogos: null,
});

export const AppSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const params = new URLSearchParams(window.location.search);
  const shouldReset = params.get('reset') === '1';

  if (shouldReset) {
    localStorage.removeItem('odyssey_settings');
  }

  const [cosmosLogos, setCosmosLogos] = useState<CosmosLogos | null>(null);

  const [settings, setSettingsState] = useState<AppSettings>(() => {
    const stored = localStorage.getItem('odyssey_settings');
    const saved = stored && !shouldReset ? JSON.parse(stored) : {};
    const runtime = getRuntimeOverrides();

    return {
      ...defaultSettings,
      ...saved,
      ...Object.fromEntries(Object.entries(runtime).filter(([_, v]) => v !== undefined)),
      templeName: detectTempleName(), // 👈 always detect from host or query param
    };
  });

  useEffect(() => {
    const fetchCosmosLogos = async () => {
      const url = [
        'https://raw.githubusercontent.com',
        settings.githubUser,
        settings.consciousness,
        settings.neuralpathway,
        settings.basePath,
        'cosmos-logos.json'
      ]
        .map(p => p.replace(/^\/|\/$/g, ''))
        .join('/');

      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const data = await res.json();
        setCosmosLogos(data);
      } catch (err) {
        console.warn('⚠️ Failed to load cosmos-logos.json', err);
      }
    };

    fetchCosmosLogos();
  }, [settings.githubUser, settings.consciousness, settings.neuralpathway, settings.basePath]);

  const setSettings = (newSettings: AppSettings) => {
    // 👇 Always ensure templeName is present
    const withTempleName = {
      ...newSettings,
      templeName: newSettings.templeName || detectTempleName(),
    };
    setSettingsState(withTempleName);
    localStorage.setItem('odyssey_settings', JSON.stringify(withTempleName));
  };

  return (
    <AppSettingsContext.Provider value={{ settings, setSettings, cosmosLogos }}>
      {children}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = () => useContext(AppSettingsContext);